import { EmbedSignDocument } from "@documenso/embed-react";
import { Logo } from "@exhousing/design-system";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

// The self-hosted Documenso origin the signing widget is embedded from.
const DOCUMENSO_URL = import.meta.env.VITE_DOCUMENSO_URL ?? "http://localhost:8400";

const COPY = {
  nl: {
    header: "Onderteken uw document",
    sub: "Veilig ondertekenen · Balatin",
    loading: "Document laden…",
    doneTitle: "Ondertekend — bedankt",
    doneBody: "Uw handtekening is vastgelegd. U kunt deze pagina sluiten; een ondertekende kopie is naar u gemaild.",
    errTitle: "Deze link werkt niet meer",
    errBody: "Deze ondertekenlink is verlopen of al gebruikt. Neem contact op met uw makelaar voor een nieuwe link.",
    invalid: "Ongeldige ondertekenlink.",
  },
  en: {
    header: "Sign your document",
    sub: "Secure e-signature · Balatin",
    loading: "Loading your document…",
    doneTitle: "Signed — thank you",
    doneBody: "Your signature has been recorded. You can close this page; a signed copy has been emailed to you.",
    errTitle: "This link no longer works",
    errBody: "This signing link has expired or was already used. Please contact your agency for a new link.",
    invalid: "Invalid signing link.",
  },
} as const;

/**
 * Public, token-addressed signing page. The recipient signs the document inside Balatin
 * (Documenso's widget is embedded, not redirected to). The token is the capability, so
 * no login is required.
 */
export function SignPage() {
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const lang = (params.get("lang") === "nl" ? "nl" : "en") as keyof typeof COPY;
  const t = COPY[lang];

  const [phase, setPhase] = useState<"loading" | "ready" | "done" | "error">("loading");

  useEffect(() => {
    document.title = `${t.header} · Balatin`;
  }, [t.header]);

  const card = "mx-auto mt-16 max-w-md rounded-xl border border-border bg-background p-8 text-center shadow-sm";

  return (
    <div className="min-h-[100dvh] bg-neutral-100">
      <header className="border-b border-border bg-background">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-4">
          <Logo className="h-7 w-auto" />
          <div className="ml-1">
            <p className="font-display text-lg font-medium leading-none tracking-tight">
              {t.header}
            </p>
            <p className="text-xs text-muted-foreground">{t.sub}</p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-6">
        {!token ? (
          <p className="mt-16 text-center text-sm text-danger">{t.invalid}</p>
        ) : phase === "done" ? (
          <div className={card} role="status" aria-live="polite">
            <CheckCircle2 className="mx-auto mb-3 size-10 text-success" aria-hidden />
            <h1 className="font-display text-xl font-medium">{t.doneTitle}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{t.doneBody}</p>
          </div>
        ) : phase === "error" ? (
          <div className={card} role="status" aria-live="polite">
            <XCircle className="mx-auto mb-3 size-10 text-danger" aria-hidden />
            <h1 className="font-display text-xl font-medium">{t.errTitle}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{t.errBody}</p>
          </div>
        ) : (
          <div className="relative">
            {phase === "loading" && (
              <div
                className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-xl bg-neutral-100 text-muted-foreground"
                role="status"
                aria-live="polite"
              >
                <Loader2 className="size-6 animate-spin" aria-hidden />
                <span className="text-sm">{t.loading}</span>
              </div>
            )}
            <div className="overflow-hidden rounded-xl border border-border bg-background shadow-sm [&_iframe]:w-full [&_iframe]:border-0 [&_iframe]:h-[calc(100dvh-11rem)] sm:[&_iframe]:h-[80vh]">
              <EmbedSignDocument
                token={token}
                host={DOCUMENSO_URL}
                language={lang}
                onDocumentReady={() => setPhase("ready")}
                onDocumentCompleted={() => setPhase("done")}
                onDocumentRejected={() => setPhase("error")}
                onDocumentError={() => setPhase("error")}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
